/**
 * Where to send a user after sign-in when the journey outlives one page.
 *
 * Login carries the return path in `?from=`. Sign-up cannot: it ends in an
 * email, and the confirmation link usually opens in a NEW tab (so
 * sessionStorage, which is per tab, would be empty there). The path is kept in
 * localStorage instead, with a short lifetime: it exists for flows such as an
 * OAuth consent request, which expires on the server after 30 minutes anyway.
 */

const STORAGE_KEY = 'svghmi:return-to';
// Matches the OAuth interaction lifetime (backend INTERACTION_TTL) with margin
// for reading the confirmation email.
const RETURN_PATH_TTL_MS = 60 * 60 * 1000;

/**
 * Accept only a same-site path: "/x", never "//host" or "https://host", so a
 * crafted ?from= cannot turn login into an open redirect.
 * @returns {string|null}
 */
export const safeReturnPath = (value) => {
  if (typeof value !== 'string' || !value.startsWith('/')) return null;
  if (value.startsWith('//') || value.startsWith('/\\')) return null;
  return value;
};

const storage = () => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
};

/** Remember a return path for the rest of the sign-up journey. */
export const rememberReturnPath = (value) => {
  const path = safeReturnPath(value);
  const store = storage();
  if (!path || !store) return;
  try {
    const expiresAt = Date.now() + RETURN_PATH_TTL_MS;
    store.setItem(STORAGE_KEY, JSON.stringify({ path, expiresAt }));
  } catch {
    // Storage full or blocked: the journey falls back to the default landing page.
  }
};

/** The remembered path if it is still fresh, without clearing it. */
export const peekReturnPath = () => {
  const store = storage();
  if (!store) return null;
  try {
    const saved = JSON.parse(store.getItem(STORAGE_KEY) || 'null');
    if (!saved || saved.expiresAt < Date.now()) {
      store.removeItem(STORAGE_KEY);
      return null;
    }
    return safeReturnPath(saved.path);
  } catch {
    return null;
  }
};

/**
 * The analytics label of the journey a return path belongs to. The OAuth
 * consent page sends signed-out visitors to /auth-login?from=/oauth/consent?…,
 * and sign-up remembers the same path for the confirmation email, so the path
 * alone tells an OAuth connection apart — without putting the interaction id
 * (or anything else from the URL) into GA.
 * @returns {'oauth'|undefined}
 */
export const authFlowOf = (path) => (
  typeof path === 'string' && path.startsWith('/oauth/') ? 'oauth' : undefined
);

/** Forget the remembered path (the user got there, or it no longer applies). */
export const clearReturnPath = () => {
  try {
    storage()?.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clean up.
  }
};
